"use client";
import { useRef } from "react";
import type { StatsigUser, StatsigEvent } from "@statsig/js-client";
import { STATSIG_EVENTS } from "../events";
import { useOnVisible } from "./use-on-visible";

interface LogEventClient {
  logEvent(event: StatsigEvent): void;
  getContext?(): { user?: { custom?: Record<string, unknown> } };
}

const logRouteImpression = (client: LogEventClient, location: string) => {
  let entrypoint = location;
  try {
    entrypoint = location.replace(/^\//, "");
  } catch (e) {
    entrypoint = "error parsing route";
  }

  // Point-in-time snapshot of subscription_status, mirrored from user.custom
  // (set by StatsigUserPropertiesSync). May be absent on the first impression
  // if the user-properties sync hasn't flushed yet.
  const subscriptionStatus = client.getContext?.()?.user?.custom
    ?.subscription_status;

  const metadata: Record<string, string> = {
    route: location,

    // remove leading slash from route
    entrypoint: entrypoint,
  };
  if (typeof subscriptionStatus === "string") {
    metadata.subscription_status = subscriptionStatus;
  }

  // Standard event naming: Event = "Impression", Value = route path (KIN-175)
  client.logEvent({
    eventName: STATSIG_EVENTS.IMPRESSION,
    value: location,
    metadata,
  });

  console.log("Logging impression event with data: ", metadata);
};

/**
 * Log one `Impression` per route, gated on the page actually being on screen —
 * riding the same {@link useOnVisible} signal as `screen_viewed` and
 * `content_visible` (PE-1775).
 *
 * Owlet warm-loads Felt pages at app launch, so firing on mount counted — and
 * billed for — impressions nobody saw, and re-counted them on every parked
 * client-side navigation.
 *
 * Gated here rather than relying on the sink gate in `useStatsigClient`,
 * because `StatsigObserver` hands this hook the **raw** client from
 * `@statsig/react-bindings` — it needs `updateUserSync`/`getContext` — so these
 * events never pass through that wrapper.
 */
const useImpressionLogger = (
  client: LogEventClient,
  location: string,
  user: StatsigUser
) => {
  // The last route actually logged. `useOnVisible` calls back on every visible
  // tick (layout, visibilitychange), so dedup lives here — same shape as
  // `useScreenViewed`.
  const loggedRoute = useRef<string | null>(null);

  useOnVisible(
    () => {
      if (!user.userID || loggedRoute.current === location) return;
      loggedRoute.current = location;
      logRouteImpression(client, location);
    },
    { watch: [location, user.userID] }
  );
};

export default useImpressionLogger;
